import * as Sentry from '@sentry/react'

import { runtimeBudgetResponseSchema, runtimeBudgetUpdateResponseSchema } from './schemas'

// v4 GET /runtime_budget -> FE domain model, replacing v3's ad hoc field pick
// CampaignConfigProvider.jsx did inline off fetchCurrentCampaignRuntimeAndBudget's
// response (start_date/end_date/total_budget/min_daily_budget/min_budget/
// max_budget/currency/has_permission_error) — same field set, v4 just adds
// new_daily_budget/platforms/timezone/task_details onto it.
export function mapRuntimeBudget(data) {
  const parsed = runtimeBudgetResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(parsed.error)
  }
  const source = (parsed.success ? parsed.data : data) ?? {}

  return {
    totalBudget: source.total_budget ?? null,
    startDate: source.start_date ?? null,
    endDate: source.end_date ?? null,
    newDailyBudget: source.new_daily_budget ?? null,
    minDailyBudget: source.min_daily_budget ?? null,
    minBudget: source.min_budget ?? null,
    maxBudget: source.max_budget ?? null,
    currency: source.currency ?? null,
    platforms: source.platforms ?? [],
    timezone: source.timezone ?? null,
    taskDetails: source.task_details ?? null,
    hasPermissionError: !!source.has_permission_error,
    permissionErrorPlatform: source.permission_error_platform ?? null,
    errorCode: source.error_code ?? null,
  }
}

// v4 POST /runtime_budget response -> FE domain model. Same shape for both
// dry_run=true (preview) and dry_run=false (commit) — only difference is
// whether taskDetails is populated (see schemas.js's updateTaskDetailsSchema
// note on why it's null for both dry-run and a failed-but-swallowed IW
// dispatch).
export function mapRuntimeBudgetUpdateResult(data) {
  const parsed = runtimeBudgetUpdateResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(parsed.error)
  }
  const source = (parsed.success ? parsed.data : data) ?? {}

  return {
    newStartDate: source.new_start_date ?? null,
    newEndDate: source.new_end_date ?? null,
    newDailyBudget: source.new_daily_budget ?? null,
    currency: source.currency ?? null,
    platforms: source.platforms ?? [],
    timezone: source.timezone ?? null,
    taskDetails: source.task_details ?? null,
  }
}

// task_details.args.budget is a plain number on the legacy FB-only path, but a
// per-platform breakdown on the multi-platform path — summing it would mix
// currencies. Prefer args.total_budget (the single operator-facing total); fall
// back to args.budget only when it's already a plain number.
export function resolveTaskDisplayBudget(args) {
  if (!args) return null
  if (typeof args.total_budget === 'number') return args.total_budget
  if (typeof args.budget === 'number') return args.budget
  return null
}

// Maps the raw v4 error body (unwrapEnvelope's `error` — {message, error_code,
// data?}, per app/core/errors/fd_abort) onto the same {message, code, status,
// data} shape CampaignConfigProvider.jsx's reducer.setError/ErrorModal.jsx
// already read off axios errors (data.code||data.error_code, data.message,
// data.data) — no new error-display convention introduced, this just makes
// sure all 7 codes survive distinctly instead of collapsing to one generic
// message.
//
// - timezoneMismatch's `data` is `{fb, google}` (both accounts' timezones).
// - dailyBudgetTooLow's `data` is `{min, currency}` (minimum total budget required).
export function mapRuntimeBudgetError(error, status) {
  const body = error?.response?.data ?? error
  return {
    message: body?.message ?? body?.error_message ?? 'Unknown error',
    code: body?.error_code ?? body?.code ?? null,
    status: status ?? error?.response?.status ?? null,
    data: body?.data,
  }
}
